import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import {
  ENEMY_ARCHETYPES,
  isDamageBlockedByShield,
  calculateTurretAim,
  type EnemyArchetype,
  type EnemyArchetypeDefinition,
  type LootEntry,
} from './archetypes'
import { EnemyAIController } from './enemy-ai'
import type { NavMeshQuery } from './navmesh'

export interface EnemyInstance {
  id: string
  archetype: EnemyArchetypeDefinition
  hp: number
  alive: boolean
  position: THREE.Vector3
  facingYaw: number
  ai: EnemyAIController
  meshGroup: THREE.Group
  body: RAPIER.RigidBody | null
  collider: RAPIER.Collider | null
  controller: RAPIER.KinematicCharacterController | null
  fireCooldown: number
  droppedLoot?: string | null
  applyDamage: (
    amount: number,
    incomingDir: THREE.Vector3,
    isExplosive: boolean,
  ) => { blocked: boolean; died: boolean; drop?: string | null }
}

export type EnemyFireCallback = (
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  speed: number,
  damage: number,
  color: number,
) => void

/**
 * Selects a loot drop from an array of weighted loot entries.
 */
export function selectLootDrop(
  lootTable?: LootEntry[],
  rng: () => number = Math.random,
): string | null {
  if (!lootTable || lootTable.length === 0) return null
  const totalWeight = lootTable.reduce((sum, entry) => sum + entry.weight, 0)
  if (totalWeight <= 0) return null

  const roll = rng() * totalWeight
  let cumulative = 0
  for (const entry of lootTable) {
    cumulative += entry.weight
    if (roll < cumulative) {
      return entry.item
    }
  }
  return lootTable[lootTable.length - 1].item
}

export class EnemyManager {
  readonly enemies: EnemyInstance[] = []
  private readonly scene: THREE.Scene
  private idCounter = 0

  constructor(scene: THREE.Scene) {
    this.scene = scene
  }

  spawnEnemy(
    archetypeKey: EnemyArchetype,
    spawnPos: THREE.Vector3,
    navmesh: NavMeshQuery,
    world?: RAPIER.World,
    RAPIER_REF?: typeof RAPIER,
  ): EnemyInstance {
    const archetype = ENEMY_ARCHETYPES[archetypeKey]
    const id = `enemy-${++this.idCounter}-${archetypeKey}`

    const meshGroup = new THREE.Group()
    meshGroup.position.copy(spawnPos)

    // Build visual archetype representation
    const [w, h] = archetype.size
    const baseMat = new THREE.MeshStandardMaterial({
      color: archetype.color,
      roughness: 0.4,
      metalness: 0.6,
    })

    if (archetype.isFlying) {
      // Scout Drone / Flying Swarmer: Disc body + glowing optic
      const droneBody = new THREE.Mesh(new THREE.CylinderGeometry(w * 0.5, w * 0.5, h, 16), baseMat)
      const optic = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 12, 12),
        new THREE.MeshStandardMaterial({ color: 0x00ffff, emissive: 0x00ffff, emissiveIntensity: 1.0 }),
      )
      optic.position.set(0, 0, -w * 0.4)
      meshGroup.add(droneBody, optic)
    } else if (archetype.isTurret) {
      // Defense / Sniper Turret: Base + swivel sphere + barrel
      const base = new THREE.Mesh(new THREE.CylinderGeometry(w * 0.6, w * 0.6, 0.3, 16), baseMat)
      const turretHead = new THREE.Mesh(
        new THREE.SphereGeometry(w * 0.4, 16, 16),
        new THREE.MeshStandardMaterial({ color: 0x222222, metalness: 0.9, roughness: 0.2 }),
      )
      turretHead.position.y = 0.4
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.08, 0.08, 0.6, 12),
        new THREE.MeshStandardMaterial({ color: 0xff3300, emissive: 0xff3300 }),
      )
      barrel.rotation.x = Math.PI / 2
      barrel.position.set(0, 0.4, -0.4)
      meshGroup.add(base, turretHead, barrel)
    } else {
      // Trooper / Enforcer / Boss
      const body = new THREE.Mesh(new THREE.CapsuleGeometry(w * 0.4, h * 0.6, 8, 16), baseMat)
      body.position.y = h * 0.5
      meshGroup.add(body)

      if (archetype.hasRiotShield) {
        // Physical riot shield mesh
        const shieldGeo = new THREE.BoxGeometry(0.8, 1.4, 0.1)
        const shieldMat = new THREE.MeshStandardMaterial({
          color: 0x00e5ff,
          transparent: true,
          opacity: 0.7,
          metalness: 0.9,
          roughness: 0.1,
        })
        const shield = new THREE.Mesh(shieldGeo, shieldMat)
        shield.position.set(0, h * 0.5, -w * 0.5)
        meshGroup.add(shield)
      }
    }

    this.scene.add(meshGroup)

    // AI Controller
    const ai = new EnemyAIController(spawnPos, {
      navMesh: navmesh,
      patrolRoute: [spawnPos.clone()],
      moveSpeed: archetype.moveSpeed,
      arrivalRadius: archetype.isTurret ? 0.0 : 0.4,
    })

    // Physics Controller
    let body: RAPIER.RigidBody | null = null
    let collider: RAPIER.Collider | null = null
    let controller: RAPIER.KinematicCharacterController | null = null

    if (world && RAPIER_REF) {
      const bDesc = RAPIER_REF.RigidBodyDesc.kinematicPositionBased().setTranslation(
        spawnPos.x,
        spawnPos.y + h * 0.5,
        spawnPos.z,
      )
      body = world.createRigidBody(bDesc)
      const colDesc = RAPIER_REF.ColliderDesc.capsule(h * 0.3, w * 0.4)
      collider = world.createCollider(colDesc, body)
      controller = world.createCharacterController(0.01)
      controller.enableAutostep(0.4, 0.2, true)
    }

    const instance: EnemyInstance = {
      id,
      archetype,
      hp: archetype.maxHp ?? archetype.HP,
      alive: true,
      position: spawnPos.clone(),
      facingYaw: 0,
      ai,
      meshGroup,
      body,
      collider,
      controller,
      fireCooldown: 0,
      applyDamage: (amount: number, incomingDir: THREE.Vector3, isExplosive: boolean) => {
        if (!instance.alive) return { blocked: false, died: false }

        const facingVec = new THREE.Vector3(-Math.sin(instance.facingYaw), 0, -Math.cos(instance.facingYaw))
        if (instance.archetype.hasRiotShield && isDamageBlockedByShield(facingVec, incomingDir, isExplosive)) {
          return { blocked: true, died: false }
        }

        instance.hp = Math.max(0, instance.hp - amount)
        if (instance.hp <= 0) {
          instance.alive = false
          meshGroup.visible = false
          const drop = instance.archetype.lootTable
            ? selectLootDrop(instance.archetype.lootTable)
            : null
          instance.droppedLoot = drop
          return { blocked: false, died: true, drop }
        }
        return { blocked: false, died: false }
      },
    }

    this.enemies.push(instance)
    return instance
  }

  update(
    dt: number,
    playerPos: THREE.Vector3,
    _world: RAPIER.World | null,
    onFireProjectile?: EnemyFireCallback,
  ) {
    const toPlayer = new THREE.Vector3()
    const muzzlePos = new THREE.Vector3()
    const fireDir = new THREE.Vector3()

    for (let i = 0; i < this.enemies.length; i++) {
      const enemy = this.enemies[i]
      if (!enemy.alive) continue

      toPlayer.subVectors(playerPos, enemy.position)
      const dist = toPlayer.length()

      // Line of sight / sighting check
      if (dist < enemy.archetype.attackRange && playerPos.y > -2.0) {
        enemy.ai.reportSighting(playerPos)
      }

      if (!enemy.archetype.isTurret) {
        const prevPos = enemy.position.clone()
        enemy.ai.update(dt)
        enemy.position.copy(enemy.ai.position)

        const dx = enemy.position.x - prevPos.x
        const dz = enemy.position.z - prevPos.z
        if (Math.hypot(dx, dz) > 0.0001) {
          enemy.facingYaw = Math.atan2(-dx, -dz)
        }
      } else {
        // Turret 3D tracking
        const { yaw } = calculateTurretAim(enemy.position, playerPos)
        enemy.facingYaw = yaw
      }

      enemy.meshGroup.position.copy(enemy.position)
      enemy.meshGroup.rotation.y = enemy.facingYaw

      if (enemy.body) {
        enemy.body.setNextKinematicTranslation({
          x: enemy.position.x,
          y: enemy.position.y + enemy.archetype.size[1] * 0.5,
          z: enemy.position.z,
        })
      }

      // Attack firing
      if (enemy.fireCooldown > 0) {
        enemy.fireCooldown = Math.max(0, enemy.fireCooldown - dt)
      }

      if (enemy.ai.state === 'attack' && dist < enemy.archetype.attackRange && enemy.fireCooldown <= 0) {
        enemy.fireCooldown = 1.0 / enemy.archetype.fireRate

        muzzlePos.copy(enemy.position).add(new THREE.Vector3(0, enemy.archetype.size[1] * 0.5, 0))
        fireDir.subVectors(playerPos, muzzlePos).normalize()

        onFireProjectile?.(
          muzzlePos,
          fireDir,
          enemy.archetype.projectileSpeed,
          enemy.archetype.projectileDamage,
          enemy.archetype.color,
        )
      }
    }
  }

  render(_alpha: number) {
    // Smooth visual interpolation if needed
  }
}
